import { plaidService } from "../external";
import { isAbortError } from "../utils/abort";
import { Debt } from "../../lib/types";

export interface PlaidCreditCard extends Debt {
  isPlaidAccount: true;
  plaidAccountId: string;
  lastSynced: Date;
  interestRate?: number;
}

// Cache configuration
const CACHE_KEY = "plaid_credit_cards_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

interface CachedData {
  data: PlaidCreditCard[];
  timestamp: number;
}

export const plaidCreditCardService = {
  /**
   * Fetch credit card accounts from Plaid and convert to Debt format
   * Uses 5-minute cache to minimize API calls
   */
  getCreditCardAccounts: async (
    forceRefresh: boolean = false
  ): Promise<PlaidCreditCard[]> => {
    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp }: CachedData = JSON.parse(cached);
            const age = Date.now() - timestamp;

            if (age < CACHE_DURATION) {
              // Convert date strings back to Date objects
              return data.map((card) => ({
                ...card,
                lastSynced: new Date(card.lastSynced),
              }));
            }
          } catch (e) {
            // Cache parse error, will fetch fresh data
          }
        }
      }

      // Fetch fresh data from Plaid
      const accounts = await plaidService.getAccounts();

      // Filter for credit card accounts
      const creditCards = accounts.filter(
        (account) =>
          account.type === "credit" || account.subtype === "credit card"
      );

      // Convert to Debt format matching existing Debt interface
      const plaidCreditCards = creditCards.map((account) => {
        const balance = Math.abs(account.balances.current || 0);
        const minimumPayment =
          plaidCreditCardService.estimateMinimumPayment(balance);

        return {
          id: `plaid-${account.account_id}`,
          plaidAccountId: account.account_id,
          account: account.mask ? `****${account.mask}` : "Unknown", // Card number (last 4 digits)
          creditor: account.official_name || account.name, // Bank/creditor name
          type: "Credit Card",
          balance,
          creditLimit: account.balances.limit || 0,
          interestRate: 0, // Plaid doesn't provide this
          maxMonthlyPayment: minimumPayment, // Plaid doesn't provide minimum payment, so we estimate
          payoffStrategy: "Minimum Payment",
          creditAffecting: "Yes",
          dueDate: "", // Plaid doesn't provide due dates
          status: "Active",
          statementCredits: 0,
          paydownAmount: 0,
          isPlaidAccount: true as const,
          lastSynced: new Date(),
        };
      });

      // Cache the results
      const cacheData: CachedData = {
        data: plaidCreditCards,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

      return plaidCreditCards;
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] Plaid credit cards aborted", error);
        return [];
      }
      console.error("Error fetching Plaid credit cards:", error);
      return [];
    }
  },

  /**
   * Check if a manual debt might be a duplicate of a Plaid account
   */
  findPotentialDuplicates: (
    manualDebts: Debt[],
    plaidCards: PlaidCreditCard[]
  ): Array<{ manual: Debt; plaid: PlaidCreditCard }> => {
    const duplicates: Array<{ manual: Debt; plaid: PlaidCreditCard }> = [];

    // Filter for only manual debts (not from Plaid)
    const trulyManualDebts = manualDebts.filter(
      (debt) => !debt.id.startsWith("plaid-")
    );

    for (const manual of trulyManualDebts) {
      if (manual.type !== "Credit Card") continue;

      for (const plaid of plaidCards) {
        // Check for name similarity (case-insensitive, partial match)
        const manualName = [manual.creditor, manual.account]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const plaidName = [plaid.creditor, plaid.account]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const isNameMatch =
          manualName.includes(plaidName) ||
          plaidName.includes(manualName) ||
          // Check for brand names (Chase, Capital One, etc.)
          manualName.split(" ").some((word) => {
            return word.length > 3 && plaidName.includes(word);
          });

        // Check for balance similarity (within 10%)
        const balanceDiff = Math.abs(manual.balance - plaid.balance);
        const balanceThreshold = Math.max(manual.balance, plaid.balance) * 0.1;
        const isBalanceSimilar = balanceDiff <= balanceThreshold;

        if (isNameMatch && isBalanceSimilar) {
          duplicates.push({ manual, plaid });
        }
      }
    }

    return duplicates;
  },

  /**
   * Calculate credit utilization for a credit card
   */
  calculateUtilization: (card: PlaidCreditCard): number => {
    if (!card.creditLimit || card.creditLimit === 0) return 0;
    return (card.balance / card.creditLimit) * 100;
  },

  /**
   * Estimate minimum payment (typically 1-3% of balance or $25, whichever is higher)
   */
  estimateMinimumPayment: (balance: number): number => {
    const percentBased = balance * 0.02; // 2% of balance
    const minimum = 25; // $25 minimum
    return Math.max(percentBased, minimum);
  },
};
